import type { ChangeEvent, MouseEvent } from "react";
import { useCallback, useState } from "react";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DoneIcon from "@mui/icons-material/Done";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import type { TextFieldProps } from "@mui/material/TextField";
import { matchSorter } from "match-sorter";
import { useTranslation } from "react-i18next";

import { emojiOptions } from "./emojiOptions";

// match individual emoji including text-presentation ones (⚠, ☎, etc) with ZWJ + skin tones
const emojiPattern = /\p{Extended_Pictographic}(?:\ufe0f|\u200d\p{Extended_Pictographic})*/gu;
const stripNonEmoji = (str: string): string => {
  return (str.match(emojiPattern) ?? []).join("");
};

export type EmojiSelectProps = {
  value: string | null;
  onChange: (value: string | null) => void;
  label?: string;
  name?: string;
  size?: "small" | "medium";
} & Omit<TextFieldProps, "value" | "onChange" | "select" | "label" | "name">;

export const EmojiSelect = ({
  value,
  onChange,
  label,
  name,
  size = "small",
  ...textFieldProps
}: EmojiSelectProps) => {
  const { t } = useTranslation(["common"]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const resolvedLabel = label ?? t("common:emoji");

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setSearch("");
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearch("");
  }, []);

  const handleSelect = useCallback(
    (emoji: string) => {
      onChange((value ?? "") + emoji);
    },
    [value, onChange],
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const filtered = stripNonEmoji(event.target.value);
      onChange(filtered.length > 0 ? filtered : null);
    },
    [onChange],
  );

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const handleEmojiClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const emoji = event.currentTarget.dataset.emoji;
      if (emoji) {
        handleSelect(emoji);
      }
    },
    [handleSelect],
  );

  const filtered = search ? matchSorter(emojiOptions, search, { keys: ["label", "emoji"] }) : emojiOptions;

  return (
    <>
      <TextField
        {...textFieldProps}
        name={name}
        label={resolvedLabel}
        size={size}
        value={value ?? ""}
        onChange={handleInputChange}
        sx={{ width: 120, ...textFieldProps.sx }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleOpen} size="small" edge="end">
                  <AddCircleOutlineIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          },
          htmlInput: {
            autoComplete: "off",
          },
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 280, maxHeight: 360 } } }}
      >
        <Box
          sx={{
            p: 1,
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder={t("common:search_emoji")}
            value={search}
            onChange={handleSearchChange}
            slotProps={{ htmlInput: { autoComplete: "off" } }}
          />
          <IconButton onClick={handleClose} size="small">
            <DoneIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, p: 1 }}>
          {filtered.map((opt) => (
            <Box
              key={opt.emoji}
              data-emoji={opt.emoji}
              onClick={handleEmojiClick}
              title={opt.label}
              sx={{
                cursor: "pointer",
                fontSize: "1.5rem",
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              {opt.emoji}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};
